/**
 * Mine (矿场) - Rocky cave entrance in a hill
 * Features: Large rock formation, cave opening with fire glow, scattered rocks
 */

import * as THREE from 'three';

export function createMineMesh(
  group: THREE.Group,
  mainColor: THREE.Color
): void {
  const darkRockMat = new THREE.MeshStandardMaterial({ color: 0x4a4a4a, roughness: 0.95 });
  const midRockMat = new THREE.MeshStandardMaterial({ color: 0x5a5a5a, roughness: 0.9 });
  const lightRockMat = new THREE.MeshStandardMaterial({ color: 0x6a6a6a, roughness: 0.85 });
  const woodMat = new THREE.MeshStandardMaterial({ color: 0x5d4037, roughness: 0.8 });
  const fireMat = new THREE.MeshStandardMaterial({
    color: 0xff6600,
    emissive: 0xff4400,
    emissiveIntensity: 0.7
  });
  const glowMat = new THREE.MeshStandardMaterial({
    color: 0xffaa55,
    emissive: 0xff6600,
    emissiveIntensity: 0.5
  });

  // === Main Rock Formation (Hill) ===
  // Large central rock mass
  const mainRockGeom = new THREE.DodecahedronGeometry(1.2, 1);
  mainRockGeom.scale(1.3, 0.8, 1.0);
  const mainRock = new THREE.Mesh(mainRockGeom, darkRockMat);
  mainRock.position.set(0, 0.7, -0.3);
  mainRock.rotation.set(0.1, 0.3, 0);
  mainRock.castShadow = true;
  group.add(mainRock);

  // Top rocks
  const topRock1Geom = new THREE.DodecahedronGeometry(0.5, 0);
  topRock1Geom.scale(1.1, 0.7, 0.9);
  const topRock1 = new THREE.Mesh(topRock1Geom, midRockMat);
  topRock1.position.set(-0.3, 1.3, -0.2);
  topRock1.rotation.set(0.2, 0.5, 0.1);
  topRock1.castShadow = true;
  group.add(topRock1);

  const topRock2Geom = new THREE.DodecahedronGeometry(0.45, 0);
  topRock2Geom.scale(0.9, 0.8, 1.0);
  const topRock2 = new THREE.Mesh(topRock2Geom, lightRockMat);
  topRock2.position.set(0.35, 1.4, -0.4);
  topRock2.rotation.set(-0.1, 0.2, 0.15);
  topRock2.castShadow = true;
  group.add(topRock2);

  // Side rocks - left
  const leftRock1Geom = new THREE.DodecahedronGeometry(0.55, 0);
  leftRock1Geom.scale(1.0, 0.7, 0.8);
  const leftRock1 = new THREE.Mesh(leftRock1Geom, darkRockMat);
  leftRock1.position.set(-1.0, 0.4, 0.1);
  leftRock1.rotation.set(0.1, 0.7, 0.2);
  leftRock1.castShadow = true;
  group.add(leftRock1);

  const leftRock2Geom = new THREE.DodecahedronGeometry(0.35, 0);
  leftRock2Geom.scale(0.9, 0.6, 0.7);
  const leftRock2 = new THREE.Mesh(leftRock2Geom, midRockMat);
  leftRock2.position.set(-0.85, 0.85, 0.2);
  leftRock2.rotation.set(0, 0.4, 0.1);
  leftRock2.castShadow = true;
  group.add(leftRock2);

  // Side rocks - right
  const rightRock1Geom = new THREE.DodecahedronGeometry(0.5, 0);
  rightRock1Geom.scale(1.0, 0.75, 0.85);
  const rightRock1 = new THREE.Mesh(rightRock1Geom, midRockMat);
  rightRock1.position.set(0.95, 0.35, 0.15);
  rightRock1.rotation.set(0.15, -0.5, -0.1);
  rightRock1.castShadow = true;
  group.add(rightRock1);

  const rightRock2Geom = new THREE.DodecahedronGeometry(0.3, 0);
  rightRock2Geom.scale(0.85, 0.6, 0.7);
  const rightRock2 = new THREE.Mesh(rightRock2Geom, lightRockMat);
  rightRock2.position.set(0.8, 0.75, 0.0);
  rightRock2.rotation.set(0, -0.3, 0);
  rightRock2.castShadow = true;
  group.add(rightRock2);

  // Front small rocks
  const frontRock1Geom = new THREE.DodecahedronGeometry(0.25, 0);
  frontRock1Geom.scale(0.9, 0.6, 0.8);
  const frontRock1 = new THREE.Mesh(frontRock1Geom, darkRockMat);
  frontRock1.position.set(-0.6, 0.15, 0.7);
  frontRock1.rotation.set(0.1, 0.8, 0);
  group.add(frontRock1);

  const frontRock2Geom = new THREE.DodecahedronGeometry(0.2, 0);
  frontRock2Geom.scale(0.85, 0.55, 0.7);
  const frontRock2 = new THREE.Mesh(frontRock2Geom, midRockMat);
  frontRock2.position.set(0.55, 0.12, 0.65);
  frontRock2.rotation.set(0, -0.5, 0.1);
  group.add(frontRock2);

  // Very small scattered rocks
  const smallRockPositions = [
    { x: -0.35, z: 0.85, s: 0.12 },
    { x: 0.25, z: 0.9, s: 0.1 },
    { x: -0.9, z: 0.55, s: 0.15 },
    { x: 0.85, z: 0.5, s: 0.13 },
    { x: 0.1, z: 0.75, s: 0.08 },
  ];
  for (const pos of smallRockPositions) {
    const smallRockGeom = new THREE.DodecahedronGeometry(pos.s, 0);
    smallRockGeom.scale(0.9, 0.5, 0.8);
    const smallRock = new THREE.Mesh(smallRockGeom, Math.random() > 0.5 ? darkRockMat : midRockMat);
    smallRock.position.set(pos.x, pos.s * 0.5, pos.z);
    smallRock.rotation.set(Math.random() * 0.5, Math.random() * Math.PI, 0);
    group.add(smallRock);
  }

  // === Cave Entrance ===
  // Dark cave background
  const caveBackGeom = new THREE.CircleGeometry(0.5, 8);
  const caveBackMat = new THREE.MeshStandardMaterial({ color: 0x0a0a0a });
  const caveBack = new THREE.Mesh(caveBackGeom, caveBackMat);
  caveBack.position.set(0, 0.5, 0.35);
  caveBack.scale.set(1.0, 0.85, 1);
  group.add(caveBack);

  // Cave entrance arch (rocks around opening)
  const archRockPositions = [
    { x: -0.45, y: 0.3, s: 0.22, mat: darkRockMat },
    { x: 0.45, y: 0.3, s: 0.2, mat: darkRockMat },
    { x: -0.35, y: 0.7, s: 0.18, mat: midRockMat },
    { x: 0.35, y: 0.7, s: 0.17, mat: midRockMat },
    { x: 0, y: 0.9, s: 0.22, mat: darkRockMat },
    { x: -0.2, y: 0.85, s: 0.15, mat: lightRockMat },
    { x: 0.2, y: 0.88, s: 0.14, mat: lightRockMat },
  ];
  for (const pos of archRockPositions) {
    const archRockGeom = new THREE.DodecahedronGeometry(pos.s, 0);
    archRockGeom.scale(0.9, 0.7, 0.5);
    const archRock = new THREE.Mesh(archRockGeom, pos.mat);
    archRock.position.set(pos.x, pos.y, 0.5);
    archRock.rotation.set(0, Math.random() * 0.5, 0);
    group.add(archRock);
  }

  // === Wooden Support Beams ===
  const beamGeom = new THREE.BoxGeometry(0.08, 0.75, 0.08);
  const leftBeam = new THREE.Mesh(beamGeom, woodMat);
  leftBeam.position.set(-0.3, 0.38, 0.55);
  leftBeam.rotation.z = 0.08;
  group.add(leftBeam);

  const rightBeam = new THREE.Mesh(beamGeom.clone(), woodMat);
  rightBeam.position.set(0.3, 0.38, 0.55);
  rightBeam.rotation.z = -0.08;
  group.add(rightBeam);

  const topBeamGeom = new THREE.BoxGeometry(0.7, 0.08, 0.08);
  const topBeam = new THREE.Mesh(topBeamGeom, woodMat);
  topBeam.position.set(0, 0.78, 0.55);
  group.add(topBeam);

  // === Fire/Glow Inside Cave ===
  // Inner glow
  const innerGlowGeom = new THREE.SphereGeometry(0.25, 8, 6);
  const innerGlow = new THREE.Mesh(innerGlowGeom, glowMat);
  innerGlow.position.set(0, 0.4, 0.2);
  group.add(innerGlow);

  // Fire flames
  const flame1Geom = new THREE.ConeGeometry(0.08, 0.25, 5);
  const flame1 = new THREE.Mesh(flame1Geom, fireMat);
  flame1.position.set(-0.08, 0.35, 0.25);
  group.add(flame1);

  const flame2Geom = new THREE.ConeGeometry(0.1, 0.3, 5);
  const flame2 = new THREE.Mesh(flame2Geom, fireMat);
  flame2.position.set(0.05, 0.38, 0.22);
  group.add(flame2);

  const flame3Geom = new THREE.ConeGeometry(0.06, 0.2, 5);
  const flame3 = new THREE.Mesh(flame3Geom, fireMat);
  flame3.position.set(0.12, 0.32, 0.28);
  group.add(flame3);

  // Embers (small glowing spheres)
  const emberMat = new THREE.MeshStandardMaterial({
    color: 0xff8800,
    emissive: 0xff6600,
    emissiveIntensity: 0.4
  });
  for (let i = 0; i < 5; i++) {
    const emberGeom = new THREE.SphereGeometry(0.025, 4, 3);
    const ember = new THREE.Mesh(emberGeom, emberMat);
    ember.position.set(
      (Math.random() - 0.5) * 0.3,
      0.2 + Math.random() * 0.2,
      0.3 + Math.random() * 0.1
    );
    group.add(ember);
  }

  // === Mining Cart Track ===
  const trackGeom = new THREE.BoxGeometry(0.06, 0.03, 1.0);
  const trackMat = new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.8 });
  const leftTrack = new THREE.Mesh(trackGeom, trackMat);
  leftTrack.position.set(-0.15, 0.02, 0.8);
  group.add(leftTrack);

  const rightTrack = new THREE.Mesh(trackGeom.clone(), trackMat);
  rightTrack.position.set(0.15, 0.02, 0.8);
  group.add(rightTrack);

  // Track cross beams
  const crossBeamGeom = new THREE.BoxGeometry(0.4, 0.02, 0.06);
  for (let i = 0; i < 4; i++) {
    const crossBeam = new THREE.Mesh(crossBeamGeom, woodMat);
    crossBeam.position.set(0, 0.01, 0.45 + i * 0.25);
    group.add(crossBeam);
  }

  // === Ore Cart ===
  const cartBodyGeom = new THREE.BoxGeometry(0.35, 0.22, 0.3);
  const cartMat = new THREE.MeshStandardMaterial({ color: 0x4a3a2a, roughness: 0.8 });
  const cartBody = new THREE.Mesh(cartBodyGeom, cartMat);
  cartBody.position.set(0, 0.18, 1.15);
  group.add(cartBody);

  // Cart wheels
  const wheelGeom = new THREE.CylinderGeometry(0.08, 0.08, 0.04, 8);
  const wheelMat = new THREE.MeshStandardMaterial({ color: 0x3a3a3a, roughness: 0.6 });
  const wheelPositions = [
    { x: -0.12, z: 1.05 }, { x: 0.12, z: 1.05 },
    { x: -0.12, z: 1.25 }, { x: 0.12, z: 1.25 }
  ];
  for (const pos of wheelPositions) {
    const wheel = new THREE.Mesh(wheelGeom, wheelMat);
    wheel.rotation.z = Math.PI / 2;
    wheel.position.set(pos.x, 0.08, pos.z);
    group.add(wheel);
  }

  // Ore in cart
  const oreMat = new THREE.MeshStandardMaterial({ color: 0x8b7355, roughness: 0.7, metalness: 0.3 });
  for (let i = 0; i < 5; i++) {
    const oreGeom = new THREE.DodecahedronGeometry(0.05 + Math.random() * 0.03, 0);
    const ore = new THREE.Mesh(oreGeom, oreMat);
    ore.position.set(
      (Math.random() - 0.5) * 0.2,
      0.32,
      1.1 + Math.random() * 0.1
    );
    ore.rotation.set(Math.random(), Math.random(), Math.random());
    group.add(ore);
  }

  // Ground base
  const groundGeom = new THREE.CircleGeometry(1.6, 16);
  const groundMat = new THREE.MeshStandardMaterial({ color: 0x4a4035, roughness: 1.0 });
  const ground = new THREE.Mesh(groundGeom, groundMat);
  ground.rotation.x = -Math.PI / 2;
  ground.position.y = 0.01;
  ground.receiveShadow = true;
  group.add(ground);
}
